import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { api } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { Button, Card, EmptyState, ErrorBox, Field, Input, Modal, PageHeader, Select, Spinner, Textarea, Toggle, useToast } from '../../components/ui';
import { RequestStatusBadge } from '../../components/status';

export function NewAbsenceModal({ open, onClose, employeeId }: { open: boolean; onClose: () => void; employeeId?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: types } = useQuery({ queryKey: ['absence-types'], queryFn: () => api.get<any[]>('/api/v1/absence-types') });
  const [f, setF] = useState({ absenceTypeId: '', startDate: '', endDate: '', partial: false, hours: 2, startTime: '09:00', reason: '' });
  const m = useMutation({
    mutationFn: () =>
      api.post('/api/v1/absences', {
        employeeId,
        absenceTypeId: f.absenceTypeId,
        startDate: f.startDate,
        endDate: f.partial ? f.startDate : f.endDate || f.startDate,
        partialMinutes: f.partial ? Math.round(f.hours * 60) : null,
        startTime: f.partial ? f.startTime : null,
        reason: f.reason || undefined,
      }),
    onSuccess: (r: any) => {
      toast.success(r.status === 'APPROVED' ? 'Ausencia registrada.' : 'Solicitud enviada.');
      qc.invalidateQueries({ queryKey: ['absences'] });
      onClose();
    },
  });
  const list = (types ?? []).filter((t) => t.active && (employeeId || t.workerCanRequest));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={employeeId ? 'Registrar ausencia' : 'Solicitar vacaciones o ausencia'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={m.isPending} disabled={!f.absenceTypeId || !f.startDate} onClick={() => m.mutate()}>
            {employeeId ? 'Registrar' : 'Enviar solicitud'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ErrorBox error={m.error} />
        <Field label="Tipo" required>
          {(id) => (
            <Select id={id} value={f.absenceTypeId} onChange={(e) => setF({ ...f, absenceTypeId: e.target.value })}>
              <option value="">Selecciona…</option>
              {list.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Toggle checked={f.partial} onChange={(v) => setF({ ...f, partial: v })} label="Ausencia por horas" description="Solo una parte de la jornada (p. ej. consulta médica)." />
        <div className="grid grid-cols-2 gap-3">
          <Field label={f.partial ? 'Fecha' : 'Desde'} required>{(id) => <Input id={id} type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} />}</Field>
          {f.partial ? (
            <Field label="Horas">{(id) => <Input id={id} type="number" min={0.25} max={12} step={0.25} value={f.hours} onChange={(e) => setF({ ...f, hours: Number(e.target.value) })} />}</Field>
          ) : (
            <Field label="Hasta">{(id) => <Input id={id} type="date" min={f.startDate} value={f.endDate} onChange={(e) => setF({ ...f, endDate: e.target.value })} />}</Field>
          )}
        </div>
        {f.partial && <Field label="Hora de inicio">{(id) => <Input id={id} type="time" value={f.startTime} onChange={(e) => setF({ ...f, startTime: e.target.value })} />}</Field>}
        <Field label="Comentario">{(id) => <Textarea id={id} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />}</Field>
      </div>
    </Modal>
  );
}

export function WorkerAbsences() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const year = new Date().getFullYear();
  const balances = useQuery({ queryKey: ['absences', 'balances', year], queryFn: () => api.get<any[]>(`/api/v1/absences/balances?year=${year}`) });
  const list = useQuery({ queryKey: ['absences', 'mine'], queryFn: () => api.get<any[]>('/api/v1/absences?mine=true') });
  const cancel = useMutation({ mutationFn: (id: string) => api.post(`/api/v1/absences/${id}/cancel`, {}), onSuccess: () => qc.invalidateQueries({ queryKey: ['absences'] }) });
  return (
    <div>
      <PageHeader
        title="Vacaciones y ausencias"
        actions={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setOpen(true)}>
            Solicitar
          </Button>
        }
      />
      <div className="mb-5 grid grid-cols-2 gap-3">
        {(balances.data ?? [])
          .filter((b) => b.type.deductsBalance)
          .map((b) => (
            <div key={b.type.id} className="card p-4">
              <p className="text-xs text-slate-500">{b.type.name}</p>
              <p className="text-2xl font-semibold tabular-nums">
                {b.available ?? '—'} <span className="text-sm font-normal text-slate-500">{b.type.unit === 'DAYS' ? 'días' : 'h'} disponibles</span>
              </p>
              <p className="text-xs text-slate-500">
                {b.entitled} asignados · {b.used} disfrutados · {b.pending} pendientes
              </p>
            </div>
          ))}
      </div>
      {list.isLoading ? (
        <Spinner />
      ) : !list.data?.length ? (
        <Card>
          <EmptyState title="Sin solicitudes" />
        </Card>
      ) : (
        <ul className="space-y-2">
          {list.data.map((a) => (
            <li key={a.id} className="card flex items-center justify-between gap-3 p-4">
              <div>
                <p className="flex items-center gap-2 font-medium">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: a.type.color }} />
                  {a.type.name}
                </p>
                <p className="text-xs text-slate-500">
                  {fmtDate(a.startDate)}
                  {a.endDate !== a.startDate ? ` – ${fmtDate(a.endDate)}` : ''} · {a.partialMinutes ? `${a.partialMinutes / 60} h` : `${a.units} ${a.type.unit === 'DAYS' ? 'días' : 'h'}`}
                </p>
                {a.reviewerComment && <p className="text-xs text-slate-500">“{a.reviewerComment}”</p>}
              </div>
              <div className="flex items-center gap-2">
                <RequestStatusBadge status={a.status} />
                {['PENDING', 'APPROVED'].includes(a.status) && (
                  <Button size="sm" variant="ghost" aria-label="Cancelar" onClick={() => cancel.mutate(a.id)}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <NewAbsenceModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
